/**
 * Customily cart actions — "Edit personalization" and "Duplicate".
 *
 * EDIT
 * Clicking "Edit personalization" sends the customer to the product page with
 * their design restored. When they add to cart, Customily creates a NEW cart
 * line and the original is left behind. This removes the original — but only
 * once the replacement is actually in the cart, so a customer who changes their
 * mind and browses away still has their original line intact.
 *
 *   1. Clicking the link stashes the line item key in sessionStorage, along
 *      with the line's customization URL — both read straight off the markup.
 *      Deliberately not on the URL, so Customily's query string is untouched.
 *   2. window.fetch is wrapped. Customily adds to cart through the theme's
 *      product-form.js, so the add is a fetch to /cart/add — the theme already
 *      intercepts it twice in custom.js for other reasons.
 *   3. On a successful add of the same product, /cart/change.js removes the
 *      original line and the add response's rendered sections are swapped for
 *      the post-removal ones, so the theme paints the final cart in one pass
 *      and the customer never sees both lines.
 *
 * Why step 1 stashes the customization URL as well as the key
 * A line item key is a hash of the line's state, and Shopify rewrites it when
 * that state changes underneath us. Wine labels are the case that exposed this:
 * the "Labels Only Discount" doesn't apply to a cart holding a single label, so
 * the moment the re-personalized replacement lands, the discount attaches to the
 * original line too and its key changes. /cart/change.js then can't find the key
 * we stashed, the removal silently fails, and the customer is left with both
 * lines — an "edit" that reads as a duplicate. So the key is treated as a hint:
 * if it's gone, the original is re-identified by its customization URL, which
 * names one saved design and which a discount doesn't touch.
 *
 * That identifier has to come off the markup. An earlier attempt fingerprinted
 * the line's properties from /cart.js at click time and it never landed — the
 * click is racing a navigation, so the page was gone before the read returned.
 * The edit link's href already IS the customization URL, so nothing is needed
 * beyond reading the attribute.
 *
 * DUPLICATE
 * Copies a line's personalization into a new line at quantity 1, so the
 * customer can duplicate then tweak the copy via "Edit personalization".
 *
 * Shopify merges an add into an existing line when the variant AND every
 * property match, which is exactly the case here — so a straight copy would
 * just bump the original to quantity 2 rather than creating the second line we
 * need. DUPLICATE_PROPERTY is what keeps them apart. It's underscore-prefixed,
 * so it stays hidden from the customer in the cart, checkout and order
 * confirmation, but it does ride along in the order data.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pwPendingPersonalizationEdit';

  /* Abandon a pending edit that's gone stale, so an add-to-cart hours later
     can't delete an unrelated line. */
  var MAX_AGE_MS = 60 * 60 * 1000;

  /* The original line may hold several bottles while the product page's
     quantity picker resets to 1. Without this, editing a qty-3 line quietly
     leaves the customer with 1. An explicit quantity choice always wins — this
     only restores when they left the new quantity at 1. */
  var RESTORE_ORIGINAL_QUANTITY = true;

  /* Forces a duplicate onto its own cart line instead of merging. */
  var DUPLICATE_PROPERTY = '_pw-duplicated-at';

  /* Customily's pointer to the saved design. It's what the edit link's href is,
     so it identifies a line without reading the cart. */
  var CUSTOMIZATION_URL_PROPERTY = '_customily-customization-url';

  /* TWO references, and the difference matters.

     chainedFetch is whatever window.fetch already is when this script loads —
     custom.js's wrapper on a Customily product page, the browser's fetch
     anywhere else. Our own wrapper below passes through to it, so every add it
     forwards still hits custom.js's "I have reviewed & confirmed" checkbox
     guard. Pointing that at the browser's fetch would silently disable the
     checkbox for the whole product page.

     pristineFetch is the browser's, captured inline in layout/theme.liquid
     before any deferred script could wrap it. ONLY the duplicate add uses it,
     because duplicating a cart line is not an add the checkbox governs: the
     customer already agreed when they added the line being copied. Without it,
     Duplicate worked everywhere except on a product page. */
  var chainedFetch = window.fetch;
  var pristineFetch = window.pwNativeFetch || window.fetch;

  /* Set while we fire a duplicate's add, so the edit interceptor below leaves
     it alone — a duplicate must never be read as "the replacement arrived". */
  var duplicating = false;

  function getPending() {
    try {
      var pending = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!pending || !pending.key) return null;
      if (Date.now() - (pending.ts || 0) > MAX_AGE_MS) {
        clearPending();
        return null;
      }
      return pending;
    } catch (error) {
      return null;
    }
  }

  function setPending(pending) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
      return true;
    } catch (error) {
      /* If we can't stash it we simply don't replace the line later. */
      return false;
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* private browsing — nothing to clean up */
    }
  }

  /* Delegated so both actions survive the cart page and drawer re-rendering
     their contents. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var editLink = target.closest('[data-customily-edit-link]');
    if (editLink) {
      rememberEdit(event, editLink);
      return;
    }

    var duplicateButton = target.closest('[data-customily-duplicate]');
    if (duplicateButton) {
      event.preventDefault();
      duplicateLine(duplicateButton);
      return;
    }

    var detailsToggle = target.closest('[data-customily-details-toggle]');
    if (detailsToggle) {
      event.preventDefault();
      toggleDetails(detailsToggle);
    }
  });

  /* --- mirrored images -------------------------------------------------------

     Customily deletes artwork on a retention clock of their own — support says
     a year, and this store's oldest orders reach that around 2026-12-22. When a
     cart thumbnail 404s, the customer sees a broken image on a product they
     personalized, which reads as "something is wrong with my order" rather than
     "the vendor expired a file". We hold a copy; this reaches for it.

     CAPTURE phase, because error events do not bubble but they do capture. One
     listener on document therefore catches every image failure on the page,
     including inside cart markup that Customily re-renders — nothing needs
     re-attaching, which is what broke earlier attempts at cart behaviour.

     Purely reactive: while their CDN is healthy this never runs. */
  var CUSTOMILY_CDN = 'https://cdn.customily.com/';

  document.addEventListener(
    'error',
    function (event) {
      var img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      /* Once per image. Our copy 404s too for anything outside previews and
         thumbs, and without this that would loop. */
      if (img.getAttribute('data-pw-mirrored')) return;

      var src = img.getAttribute('src') || '';
      if (src.indexOf(CUSTOMILY_CDN) !== 0) return;

      img.setAttribute('data-pw-mirrored', '1');
      /* Same-origin: Shopify proxies /apps/orders to the pw-orders app and signs
         the request on the way through, so no credentials ride in the markup. */
      img.src = '/apps/orders/asset/' + src.slice(CUSTOMILY_CDN.length);
    },
    true
  );

  /* --- thumbnail opens the preview ------------------------------------------

     The thumbnail linked to the base product page, which threw away the design
     the image is showing — and the product title beside it already goes there.

     Customily injects its own PREVIEW control into each personalized line at
     runtime. This forwards to that control rather than reimplementing it. An
     earlier attempt embedded a preview URL in the Liquid and opened its own
     modal; their script rewrites that markup on every cart re-render, so the
     attribute kept disappearing and the two handlers raced. Nothing is added to
     the markup here, and the listener is delegated on document, so there is
     nothing for their re-render to undo.

     When no PREVIEW control exists — an unpersonalized line — the displayed
     image is opened in our own modal instead. */

  /* CAPTURE phase, not bubble.

     The thumbnail sits inside an <a> to the product page, and the drawer has
     handlers of its own bound closer to the target. A bubble listener on
     document runs after all of them, by which point the navigation has already
     been started — the modal would appear for a moment and the page would then
     load anyway. Capture on document runs before anything else sees the click,
     so preventDefault and stopPropagation actually hold. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var media = target.closest('.cart-item__media');
      if (!media) return;

      if (handleThumbnailClick(event, media)) {
        event.stopPropagation();
      }
    },
    true
  );

  /* Cart thumbnails are served at ~90px. Blown up in the modal that is a blurry
     postage stamp, so ask Shopify's CDN for a real size instead. Anything
     without a width parameter — Customily's own renders — is already full size
     and passes through untouched. */
  function upscale(source) {
    try {
      var url = new URL(source, window.location.origin);
      if (!url.searchParams.has('width')) return source;
      url.searchParams.set('width', '1600');
      url.searchParams.delete('height');
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function customilyPreviewControl(cartItem) {
    /* Their control exposes no stable hook, so it is matched on its label.
       Leaf nodes only, and never our own actions block. */
    var candidates = cartItem.querySelectorAll('a, button, span, div, p, [role="button"]');

    for (var i = 0; i < candidates.length; i++) {
      var element = candidates[i];
      if (element.children.length) continue;
      if (element.closest('.customily-cart-item-actions')) continue;
      if ((element.textContent || '').trim().toLowerCase() !== 'preview') continue;
      return element;
    }
    return null;
  }

  /* Returns true when it handled the click. */
  function handleThumbnailClick(event, media) {
    var cartItem = media.closest('.cart-item');
    if (!cartItem) return false;

    var control = customilyPreviewControl(cartItem);
    if (control) {
      event.preventDefault();
      /* A real, bubbling click, so a delegated handler of theirs sees it even
         if nothing is bound to the element itself. */
      control.click();
      return true;
    }

    var image = media.querySelector('img');
    var source = image && (image.currentSrc || image.getAttribute('src'));
    if (!source) return false;

    event.preventDefault();
    openPreview(upscale(source));
    return true;
  }

  var previewOverlay = null;

  function previewElement() {
    if (previewOverlay) return previewOverlay;

    previewOverlay = document.createElement('div');
    previewOverlay.className = 'customily-preview-overlay';
    previewOverlay.innerHTML =
      '<button type="button" class="customily-preview-close" aria-label="Close preview">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" aria-hidden="true">' +
      '<path d="M6 6l12 12M18 6L6 18"></path></svg></button>' +
      '<img alt="Product preview">';

    /* The close button only — a click on the backdrop is swallowed rather than
       dismissing. That is how Customily's own preview behaves, and the two sit
       side by side in the same cart, so they should not close differently.
       Escape still works: it is invisible, so it cannot read as inconsistent,
       and leaving a modal with no keyboard exit would be worse. */
    previewOverlay.addEventListener('click', function (event) {
      if (event.target.closest('.customily-preview-close')) {
        closePreview();
        return;
      }
      event.stopPropagation();
    });

    document.body.appendChild(previewOverlay);
    return previewOverlay;
  }

  function openPreview(source) {
    var overlay = previewElement();
    overlay.querySelector('img').setAttribute('src', source);
    overlay.classList.add('is-open');
    overlay.querySelector('.customily-preview-close').focus();
  }

  function closePreview() {
    if (!previewOverlay) return;
    previewOverlay.classList.remove('is-open');
    /* Cleared so a reopen never flashes the previous line's image. */
    previewOverlay.querySelector('img').removeAttribute('src');
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') closePreview();
  });

  /* Read-only disclosure of the line's properties. State lives entirely in the
     DOM, so any cart re-render collapses it again — details are meant to start
     hidden every time. */
  function toggleDetails(button) {
    var wrapper = button.closest('.customily-cart-item-actions');
    var panel = wrapper && wrapper.querySelector('[data-customily-details]');
    if (!panel) return;

    var expanded = button.getAttribute('aria-expanded') === 'true';

    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    panel.hidden = expanded;
    button.textContent = expanded
      ? button.getAttribute('data-label-show') || 'Show Details'
      : button.getAttribute('data-label-hide') || 'Hide Details';
  }

  /* Everything stashed here has to come off the markup, synchronously. An
     earlier version read /cart.js first to fingerprint the line, and it never
     landed: the click is racing a navigation — Customily runs with
     interceptClicks and does its own cart handling — so the page was gone
     before the response came back, leaving only the fields written up front.

     getAttribute('href'), not link.href: we want the property value exactly as
     Customily wrote it, not the browser's absolute resolution of it. */
  function rememberEdit(event, link) {
    setPending({
      key: link.getAttribute('data-line-key'),
      productId: parseInt(link.getAttribute('data-product-id'), 10) || null,
      quantity: parseInt(link.getAttribute('data-quantity'), 10) || 1,
      customizationUrl: link.getAttribute('href'),
      duplicateMarker: link.getAttribute('data-duplicate-marker') || '',
      ts: Date.now()
    });
  }

  /* --- shared cart plumbing ------------------------------------------------ */

  function unique(values) {
    return values.filter(function (value, index) {
      return value && values.indexOf(value) === index;
    });
  }

  /* Goes through window.fetch so the theme's other cart listeners (the gift
     note upsell) still see the read. Resolves to null on anything unexpected. */
  function readCart() {
    return window
      .fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      });
  }

  function findByKey(cart, key) {
    if (!cart || !cart.items || !key) return null;
    return (
      cart.items.filter(function (item) {
        return item.key === key;
      })[0] || null
    );
  }

  function propertyOf(line, name) {
    var value = line && line.properties && line.properties[name];
    return value === undefined || value === null ? '' : String(value);
  }

  function describe(line) {
    return {
      key: line.key,
      customizationUrl: propertyOf(line, CUSTOMIZATION_URL_PROPERTY),
      duplicateMarker: propertyOf(line, DUPLICATE_PROPERTY)
    };
  }

  /* Locates a line we saw earlier: its key first, because that's exact, and
     failing that by its customization URL, which names one saved design and —
     unlike the key — doesn't change when a discount attaches to the line.

     The URL alone isn't enough once Duplicate is in play: a copy reproduces it
     verbatim, so a line and its copy look identical. DUPLICATE_PROPERTY is the
     one thing that separates them — empty on the original, distinct on each
     copy — so it's part of the match. Still never a guess between several
     candidates, and never `excludeKey`, which is the line just added. */
  function findLine(cart, descriptor, excludeKey) {
    var byKey = findByKey(cart, descriptor.key);
    if (byKey) return byKey;

    if (!cart || !cart.items || !descriptor.customizationUrl) return null;

    var candidates = cart.items.filter(function (item) {
      if (excludeKey && item.key === excludeKey) return false;
      if (propertyOf(item, CUSTOMIZATION_URL_PROPERTY) !== descriptor.customizationUrl) return false;
      return propertyOf(item, DUPLICATE_PROPERTY) === (descriptor.duplicateMarker || '');
    });

    return candidates.length === 1 ? candidates[0] : null;
  }

  function sectionInnerHTML(html, selector) {
    if (!html) return null;
    var element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  /* Which cart UI the action was clicked in decides what we re-render, and how.
     Re-rendering the drawer's sections also reopens it, so we must not take
     that path for a click on the cart page. */
  function cartContext(element) {
    var drawer = element.closest('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return {
        mode: 'drawer',
        element: drawer,
        sections: unique(
          drawer.getSectionsToRender().map(function (section) {
            return section.id;
          })
        )
      };
    }

    var items = document.querySelector('cart-items');
    if (items && typeof items.getSectionsToRender === 'function') {
      var toRender = items.getSectionsToRender();
      return {
        mode: 'page',
        toRender: toRender,
        sections: unique(
          toRender.map(function (section) {
            return section.section;
          })
        )
      };
    }

    return null;
  }

  function renderCart(context, state) {
    if (!context || !state || !state.sections) return false;

    if (context.mode === 'drawer') {
      context.element.renderContents(state);
      return true;
    }

    /* Mirrors the loop in cart.js updateQuantity(). */
    context.toRender.forEach(function (section) {
      var container = document.getElementById(section.id);
      if (!container) return;

      var elementToReplace = container.querySelector(section.selector) || container;
      var newInner = sectionInnerHTML(state.sections[section.section], section.selector);
      if (newInner !== null) elementToReplace.innerHTML = newInner;
    });

    return true;
  }

  /* Goes through window.fetch, not the captured references, so the theme's other cart
     listeners (the gift note upsell) still see the change. */
  function changeCart(payload) {
    return window
      .fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(function (response) {
        return response.ok ? response.json() : null;
      });
  }

  /* --- duplicate ----------------------------------------------------------- */

  function setBusy(button, busy) {
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
    }
  }

  function duplicateLine(button) {
    if (button.hasAttribute('aria-busy')) return;

    var key = button.getAttribute('data-line-key');
    if (!key) return;

    setBusy(button, true);

    /* Duplicating means they came back to the cart rather than finishing an
       edit, so any stashed edit is stale. */
    clearPending();

    var context = cartContext(button);
    var sections = context ? context.sections.join(',') : null;
    var sectionsUrl = window.location.pathname;
    var marker = String(Date.now());
    var expected = null;

    /* Read the properties off the live cart rather than embedding every
       Customily URL into the markup of every cart line. */
    readCart()
      .then(function (cart) {
        if (!cart) throw new Error('could not read the cart');

        var line = findByKey(cart, key);
        if (!line) throw new Error('line ' + key + ' is no longer in the cart');

        var properties = {};
        Object.keys(line.properties || {}).forEach(function (name) {
          properties[name] = line.properties[name];
        });
        properties[DUPLICATE_PROPERTY] = marker;

        expected = personalizationFingerprint(properties);

        var body = {
          id: line.variant_id,
          quantity: 1,
          properties: properties
        };

        if (sections) {
          body.sections = sections;
          body.sections_url = sectionsUrl;
        }

        /* Read synchronously by our own interceptor. The duplicate add goes
           straight to pristineFetch, so it never reaches our wrapper — this
           flag stays as belt and braces. */
        duplicating = true;
        var request = sendDuplicateAdd(body);
        duplicating = false;

        return request;
      })
      .then(function (response) {
        return response.json().then(function (state) {
          if (!response.ok || state.status) {
            throw new Error(state.description || state.message || 'add to cart failed');
          }
          return state;
        });
      })
      .then(function (state) {
        return confirmCopy(expected, marker, sections, sectionsUrl).then(function (corrected) {
          return corrected || state;
        });
      })
      .then(function (state) {
        if (!renderCart(context, state)) window.location.reload();
      })
      .catch(function (error) {
        console.warn('[customily-edit-cart] duplicate failed', error);
        setBusy(button, false);
      });
  }

  /* Customily wraps window.fetch too, and on a /cart/add it attaches whatever
     personalization is currently loaded on the page — it logs "Fetch: Adding
     Customily data" and regenerates the thumbnail. On a product page reached
     through "Edit personalization" that is a DIFFERENT design from the line
     being copied, so the duplicate came back carrying the wrong artwork.

     Sending through the fetch captured at load gets the body to Shopify as
     written. The edit flow deliberately still goes through window.fetch: there
     the customer really is personalizing on that page, so Customily's data is
     exactly what should ride along. */
  function sendDuplicateAdd(body) {
    return pristineFetch.call(window, window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
  }

  /* What the customer would notice being wrong. Underscore-prefixed properties
     are Customily's own bookkeeping — a copy legitimately gets its own
     thumbnail and customization URLs, so they're not part of the comparison. */
  function personalizationFingerprint(properties) {
    var values = properties || {};

    return Object.keys(values)
      .filter(function (name) {
        return name.charAt(0) !== '_';
      })
      .sort()
      .map(function (name) {
        return name + '\u0000' + String(values[name]);
      })
      .join('\u0001');
  }

  /* Belt and braces for the above: if anything still rewrites the add, the
     customer ends up with a personalized product they didn't ask for and could
     order without noticing. Better to undo it and leave the cart as it was.
     Resolves to a render state when it removed something, otherwise null. */
  function confirmCopy(expected, marker, sections, sectionsUrl) {
    if (!expected) return Promise.resolve(null);

    return readCart().then(function (cart) {
      if (!cart || !cart.items) return null;

      var copy = cart.items.filter(function (item) {
        return String((item.properties || {})[DUPLICATE_PROPERTY]) === marker;
      })[0];

      if (!copy) return null;

      if (personalizationFingerprint(copy.properties) === expected) {
        /* The add bypassed window.fetch, so tell the theme's cart listeners
           (the gift note upsell) what they missed. */
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return null;
      }

      console.warn(
        '[customily-edit-cart] the duplicate came back with a different personalization ' +
          'than the line it was copied from — removing it rather than leaving the customer ' +
          'a product they did not choose'
      );

      return changeCart({
        id: copy.key,
        quantity: 0,
        sections: sections,
        sections_url: sectionsUrl
      });
    });
  }

  /* --- edit: replace the original line -------------------------------------- */

  /* The add request tells us which sections the theme wants rendered, and under
     which keys. We ask /cart/change.js for exactly the same ones so the result
     is a drop-in swap. */
  function readBodyField(body, field) {
    if (!body) return null;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return body.get(field);
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return body.get(field);
      }
      if (typeof body === 'string') {
        var parsed = JSON.parse(body);
        return parsed ? parsed[field] : null;
      }
    } catch (error) {
      /* not a shape we understand — treat as absent */
    }
    return null;
  }

  function replaceOriginalLine(response, pending, sections, sectionsUrl) {
    return response
      .clone()
      .json()
      .then(function (added) {
        var addedItems = added && added.items ? added.items : [added];

        var match = addedItems.filter(function (item) {
          return item && (!pending.productId || item.product_id === pending.productId);
        })[0];

        /* A different product went into the cart — the customer wandered off
           mid-edit. Leave their original line alone. */
        if (!match) return response;

        clearPending();

        /* The add has landed, so the cart is in its final state and any key
           the discount rewrote has settled. Resolve the original line against
           that rather than against the key we stashed before the edit. */
        return readCart().then(function (cart) {
          var original = findLine(cart, pending, match.key);

          /* Either Shopify merged the add back into the original line — nothing
             about the personalization changed — or we can't tell which line was
             the original any more. Removing anything now risks deleting the item
             the customer still wants. */
          if (!original || original.key === match.key) return response;

          return removeAndRestore(response, added, original, match, pending, sections, sectionsUrl);
        });
      });
  }

  function removeAndRestore(response, added, original, match, pending, sections, sectionsUrl) {
    return changeCart({
      id: original.key,
      quantity: 0,
      sections: sections,
      sections_url: sectionsUrl
    })
      .then(function (state) {
        if (!state) return null;

        if (RESTORE_ORIGINAL_QUANTITY && pending.quantity > 1 && match.quantity === 1) {
          /* Dropping the original can move the cart back out of a
             quantity-based discount, which rewrites the replacement's key in
             turn — so re-read it from what the removal just returned. */
          var replacement = findLine(state, describe(match), null);
          if (!replacement) return state;

          return changeCart({
            id: replacement.key,
            quantity: pending.quantity,
            sections: sections,
            sections_url: sectionsUrl
          }).then(function (restored) {
            return restored || state;
          });
        }

        return state;
      })
      .then(function (state) {
        /* No sections to swap: the removal still happened, the theme just
           renders from its own (now one-line-stale) copy until the next
           page load. */
        if (!state || !sections || !state.sections) return response;

        added.sections = state.sections;

        return new Response(JSON.stringify(added), {
          status: response.status,
          statusText: response.statusText,
          headers: { 'Content-Type': 'application/json' }
        });
      });
  }

  window.fetch = function () {
    var args = arguments;
    var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    var isDuplicateAdd = duplicating;
    var result = chainedFetch.apply(this, args);

    if (isDuplicateAdd || url.indexOf('/cart/add') === -1) return result;

    var pending = getPending();
    if (!pending) return result;

    var options = args[1] || {};
    var sections = readBodyField(options.body, 'sections');
    var sectionsUrl = readBodyField(options.body, 'sections_url') || window.location.pathname;

    return result.then(function (response) {
      /* Sold out, validation error, etc. — the original line is still the
         customer's only copy, so keep it. */
      if (!response || !response.ok) return response;

      return replaceOriginalLine(response, pending, sections, sectionsUrl).catch(function (error) {
        console.warn('[customily-edit-cart] could not replace the original line', error);
        return response;
      });
    });
  };
})();
